import { Stack, Text, ActionButton, PrimaryButton, IconButton } from "@fluentui/react";
import { DismissRegular, LocationFilled, PhoneFilled, ChatFilled } from "@fluentui/react-icons";
import { useState, useEffect } from "react";
import styles from "./CarDetailDrawer.module.css";
import { capitalizeFirstLetter } from "../../utils/textUtils";
import { PriceComparison } from "./PriceComparison";
import { CarImageCarousel } from "./CarImageCarousel";
import { ImageModalCarousel } from "./ImageModalCarousel";
import { savedCarsService } from "../../api/savedCarsService";

interface VendorStats {
    vendedor_id: string;
    nombre_vendedor: string;
    total_autos_vendidos?: number;
    precio_promedio?: number;
    marcas_vendidas?: string[];
    ubicacion?: string[];
    fecha_primer_listado?: string;
    fecha_ultimo_listado?: string;
}

interface CarListing {
    id_unico: string;
    url?: string;
    marca: string;
    modelo: string;
    fecha_fabricacion: number;
    version?: string;
    precio: number;
    kilometraje: number;
    transmision: string;
    tipo_combustible: string;
    tipo_traccion?: string;
    motor?: string;
    departamento?: string;
    imagen_url?: string;
    image_url_2?: string;
    image_url_3?: string;
    image_url_4?: string;
    image_url_5?: string;
    tipo_vendedor?: string;
    precio_mercado_promedio?: number;
    precio_mercado_minimo?: number;
    precio_mercado_maximo?: number;
    vendedor_nombre?: string;
    vendedor_telefono?: string;
    descripcion?: string;
    color?: string;
    asientos_cuero?: string;
    unico_dueno?: string;
    negociable?: string;
    financiamiento?: string;
    seguridad?: string;
    opciones?: string;
    mantenimiento_status?: string;
    acepta_cambios?: string;
    transparencia?: number;
    transparencia_explicacion?: string;
    fecha_publicacion?: string;
    rebaja?: number;
    comentario_experto?: string;
    image_analysis?: string;
    mantenimiento?: string;
    mantenimiento_detalle?: string;
    financiamiento_detalle?: string;
    vendor_stats?: VendorStats;
    views_count?: number;
}

interface Props {
    car: CarListing | null;
    isOpen: boolean;
    onDismiss: () => void;
    onConversarClick?: (carContext: string) => void;
}

export const CarDetailDrawer = ({ car, isOpen, onDismiss, onConversarClick }: Props) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [showTransparencyTooltip, setShowTransparencyTooltip] = useState(false);
    const [showTransparencyExplanationTooltip, setShowTransparencyExplanationTooltip] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalInitialIndex, setModalInitialIndex] = useState(0);

    // Check if the car is saved when component mounts or car changes
    useEffect(() => {
        if (car) {
            // Check saved status from database
            savedCarsService.isCarSaved(car.id_unico)
                .then(saved => setIsSaved(saved))
                .catch(err => {
                    console.error("Error checking saved status:", err);
                    setIsSaved(false);
                });
        }
    }, [car]);

    // Record view when drawer opens with a car (fire-and-forget, non-blocking)
    useEffect(() => {
        if (car?.id_unico && isOpen) {
            fetch(`/cars/${car.id_unico}/view`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            }).catch(() => {}); // Silent fail - view tracking is non-critical
        }
    }, [car?.id_unico, isOpen]);

    const handleSaveCar = async () => {
        if (!car || isSaving) return;

        setIsSaving(true);
        try {
            if (isSaved) {
                // Remove car from saved
                await savedCarsService.unsaveCar(car.id_unico);
                setIsSaved(false);
            } else {
                // Add car to saved
                await savedCarsService.saveCar(car.id_unico);
                setIsSaved(true);
            }

            // Dispatch custom event to update saved cars count in the same tab
            window.dispatchEvent(new Event('savedCarsUpdated'));
        } catch (error) {
            console.error("Error toggling saved status:", error);
        } finally {
            setIsSaving(false);
        }
    };
    
    if (!car) return null;

    const getPriceIndicator = () => {
        if (!car.precio_mercado_promedio) return "En mercado";
        const ratio = car.precio / car.precio_mercado_promedio;
        if (ratio < 0.9) return "Por debajo del mercado";
        if (ratio > 1.1) return "Por encima del mercado";
        return "En mercado";
    };

    const getPriceDifference = () => {
        if (!car.precio_mercado_promedio) return null;
        const difference = car.precio - car.precio_mercado_promedio;
        return difference;
    };

    const getPriceDifferenceText = () => {
        const difference = getPriceDifference();
        if (difference === null) return "";
        
        const absDifference = Math.abs(difference);
        if (difference > 0) {
            return `$${Math.round(absDifference).toLocaleString('en-US', { maximumFractionDigits: 0 })} por encima del mercado`;
        } else if (difference < 0) {
            return `$${Math.round(absDifference).toLocaleString('en-US', { maximumFractionDigits: 0 })} por debajo del mercado`;
        }
        return "En precio de mercado";
    };

    const getPriceStatus = () => {
        // If no market data available, show "No disponible"
        if (!car.precio_mercado_promedio || car.precio_mercado_promedio === 0) {
            return { status: 'unavailable', label: 'No disponible', color: '#6b7280' };
        }

        // If missing maximo, also show "No disponible"
        if (!car.precio_mercado_maximo || car.precio_mercado_maximo === 0) {
            return { status: 'unavailable', label: 'No disponible', color: '#6b7280' };
        }

        // precio <= promedio: "Buen Precio" with green checkmark
        if (car.precio <= car.precio_mercado_promedio) {
            return { status: 'good', label: 'Buen Precio', color: '#22c55e' };
        }

        // precio > promedio but <= maximo: "Precio Justo" with orange checkmark
        if (car.precio > car.precio_mercado_promedio && car.precio <= car.precio_mercado_maximo) {
            return { status: 'fair', label: 'Precio Justo', color: '#ffa500' };
        }

        // precio > maximo: "Revisa Precio" with orange exclamation mark
        return { status: 'high', label: 'Revisa Precio', color: '#ffa500' };
    };

    const getPriceIndicatorColor = () => {
        const indicator = getPriceIndicator();
        switch (indicator) {
            case "Por debajo del mercado": return "#10b981"; // green
            case "Por encima del mercado": return "#ef4444"; // red
            default: return "#6b7280"; // gray
        }
    };

    const getEstimatedOwnershipCost = () => {
        // Simple estimation based on vehicle type and age
        const age = new Date().getFullYear() - car.fecha_fabricacion;
        const baseFuel = car.tipo_combustible === "diesel" ? 4962 : 5200;
        const baseMaintenance = age > 5 ? 459 * 1.5 : 459;
        const baseInsurance = car.precio * 0.02; // 2% of vehicle price

        return {
            fuel: Math.round(baseFuel),
            maintenance: Math.round(baseMaintenance),
            insurance: Math.round(baseInsurance)
        };
    };

    const ownershipCost = getEstimatedOwnershipCost();

    const getWhyItSuits = () => {
        const reasons = [];
        
        if (car.unico_dueno === "SI") {
            reasons.push("Único dueño - mayor confiabilidad");
        }
        
        if (car.precio_mercado_promedio && car.precio < car.precio_mercado_promedio * 0.9) {
            reasons.push("Precio por debajo del mercado - buena oportunidad");
        }
        
        if (car.asientos_cuero === "Si") {
            reasons.push("Asientos de cuero - mayor comodidad");
        }
        
        if (car.mantenimiento_status && car.mantenimiento_status.includes("día")) {
            reasons.push("Mantenimiento al día - menor riesgo");
        }
        
        if (reasons.length === 0) {
            reasons.push("Vehículo que cumple con tus criterios de búsqueda");
        }
        
        return reasons;
    };

    const getAlertsAndRisks = () => {
        const alerts = [];
        
        if (car.precio_mercado_promedio && car.precio < car.precio_mercado_promedio * 0.8) {
            alerts.push("Precio muy por debajo del mercado - verificar condiciones");
        }
        
        const age = new Date().getFullYear() - car.fecha_fabricacion;
        if (age > 10) {
            alerts.push("Vehículo de más de 10 años - revisar estado general");
        }
        
        if (car.kilometraje > 150000) {
            alerts.push("Alto kilometraje - revisar historial de mantenimiento");
        }
        
        if (alerts.length === 0) {
            alerts.push("No se detectaron alertas relevantes");
        }
        
        return alerts;
    };

    const getMarketContext = () => {
        const age = new Date().getFullYear() - car.fecha_fabricacion;
        
        // Calculate real days on market using fecha_publicacion
        let daysOnMarket = 0;
        if (car.fecha_publicacion) {
            const publicationDate = new Date(car.fecha_publicacion);
            const currentDate = new Date();
            const timeDifference = currentDate.getTime() - publicationDate.getTime();
            daysOnMarket = Math.floor(timeDifference / (1000 * 3600 * 24));
        }
        
        return {
            priceRange: car.precio_mercado_promedio ?
                `$${Math.round(car.precio_mercado_promedio * 0.85).toLocaleString('en-US', { maximumFractionDigits: 0 })} - $${Math.round(car.precio_mercado_promedio * 1.15).toLocaleString('en-US', { maximumFractionDigits: 0 })}` :
                "Rango no disponible",
            averagePrice: car.precio_mercado_promedio ?
                `$${Math.round(car.precio_mercado_promedio).toLocaleString('en-US', { maximumFractionDigits: 0 })}` :
                "No disponible",
            daysOnMarket: car.fecha_publicacion ? 
                `${daysOnMarket} ${daysOnMarket === 1 ? 'día' : 'días'} en mercado` :
                "Tiempo no disponible"
        };
    };

    const marketContext = getMarketContext();
    const whyItSuits = getWhyItSuits();
    const alertsAndRisks = getAlertsAndRisks();

    // Check if vendedor_telefono is a valid phone number
    const isValidPhoneNumber = (phone: string | undefined | null): boolean => {
        if (!phone) return false;

        const phoneStr = phone.toString().trim().toLowerCase();

        // Check if it's empty or contains invalid values
        if (phoneStr === '' ||
            phoneStr === 'null' ||
            phoneStr === 'undefined' ||
            phoneStr === 'llamar' ||
            phoneStr === 'n/a' ||
            phoneStr === 'no disponible') {
            return false;
        }

        // Extract only digits from the phone number
        const digitsOnly = phoneStr.replace(/\D/g, '');

        // A valid phone number should have at least 7 digits
        return digitsOnly.length >= 7;
    };

    const hasValidPhone = isValidPhoneNumber(car.vendedor_telefono);

    const handleWhatsAppContact = () => {
        if (hasValidPhone && car.vendedor_telefono) {
            // Clean the phone number - remove any non-digit characters
            let phoneNumber = car.vendedor_telefono.toString().replace(/\D/g, '');

            // Check if the number already includes country code
            // Nicaragua country code is 505
            if (!phoneNumber.startsWith('505')) {
                phoneNumber = '505' + phoneNumber;
            }

            // Create a detailed message in Spanish
            const carDetails = [];
            if (car.transmision) carDetails.push(`Transmisión: ${car.transmision}`);
            if (car.kilometraje) carDetails.push(`Kilometraje: ${car.kilometraje.toLocaleString()} km`);

            const message = encodeURIComponent(
                `¡Hola! Vi tu anuncio en Videntis.ai sobre el ${car.marca} ${car.modelo} del año ${car.fecha_fabricacion} que tienes en venta por $${Math.round(car.precio).toLocaleString('en-US', { maximumFractionDigits: 0 })}.\n\n` +
                `${carDetails.length > 0 ? 'Detalles del vehículo:\n' + carDetails.join('\n') + '\n\n' : ''}` +
                `Me gustaría obtener más información sobre el vehículo y coordinar una visita para verlo. ¿Cuándo podríamos agendarlo?\n\n` +
                `Gracias.`
            );

            // Open WhatsApp with the formatted message
            window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
        }
    };

    const handlePhoneCall = () => {
        if (car.vendedor_telefono) {
            window.open(`tel:+505${car.vendedor_telefono}`, '_self');
        }
    };

    const handleConversarClick = () => {
        if (!car || !onConversarClick) return;

        // Create a comprehensive context about the car
        const carContext = `Quiero conversar sobre este vehículo: ${car.marca} ${car.modelo} del año ${car.fecha_fabricacion}${car.version ? `, versión ${car.version}` : ''}.
Precio: $${Math.round(car.precio).toLocaleString('en-US', { maximumFractionDigits: 0 })}
Kilometraje: ${car.kilometraje?.toLocaleString()} km
Transmisión: ${car.transmision}
Combustible: ${car.tipo_combustible}
Color: ${car.color || 'No especificado'}
Ubicación: ${capitalizeFirstLetter(car.departamento)}
Vendedor: ${car.vendedor_nombre || capitalizeFirstLetter(car.tipo_vendedor)}
${car.unico_dueno === "SI" ? "Único dueño" : ""}
${car.precio_mercado_promedio ? `Precio promedio del mercado: $${Math.round(car.precio_mercado_promedio).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : ''}
${car.descripcion ? `Descripción: ${car.descripcion}` : ''}`;

        // Pass the context to the chat
        onConversarClick(carContext);

        // Close the drawer
        onDismiss();
    };

    const handleExpandImage = (index: number) => {
        setModalInitialIndex(index);
        setIsModalOpen(true);
    };

    // Get all valid image URLs
    const imageUrls = [
        car?.imagen_url,
        car?.image_url_2,
        car?.image_url_3,
        car?.image_url_4,
        car?.image_url_5,
    ].filter((url): url is string => !!url);

    return (
        <>
            {/* Custom drawer without overlay */}
            <div className={`${styles.customDrawer} ${isOpen ? styles.open : ''}`}>
                <div className={styles.drawerContent}>
                {/* Header with close button */}
                <div className={styles.drawerHeader} style={{ marginBottom: '8px' }}>
                    <div>
                        <Text className={styles.sectionTitle} style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>A simple vista</Text>
                    </div>
                    <IconButton
                        iconProps={{ iconName: "Cancel" }}
                        onClick={onDismiss}
                        className={styles.closeButton}
                        ariaLabel="Cerrar"
                    />
                </div>

                {/* Location and vendor info */}
                <div className={styles.section} style={{ paddingTop: '0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className={styles.carLocation}>
                            <div className={styles.carLocationIcon}>
                                <LocationFilled />
                            </div>
                            <div className={styles.carLocationText}>
                                <span className={styles.carLocationDepartamento}>
                                    {capitalizeFirstLetter(car.departamento)}
                                </span>
                                <span className={styles.carLocationDivider}></span>
                                <span className={styles.carLocationVendedor}>
                                    {capitalizeFirstLetter(car.tipo_vendedor)}
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {car.views_count !== undefined && car.views_count >= 5 && (
                                <div className={styles.viewsBadge}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                    <span>{car.views_count} vistas</span>
                                </div>
                            )}
                            {car.fecha_publicacion && (
                                <div className={styles.publishDateBadge}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                        <rect x="3" y="6" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M3 10H21" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M8 3V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        <path d="M16 3V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                    <span className={styles.publishDateLabel}>Publicado:</span>
                                    <span className={styles.publishDateValue}>{car.fecha_publicacion}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <CarImageCarousel
                        images={{
                            imagen_url: car.imagen_url,
                            image_url_2: car.image_url_2,
                            image_url_3: car.image_url_3,
                            image_url_4: car.image_url_4,
                            image_url_5: car.image_url_5,
                        }}
                        carTitle={`${car.marca} ${car.modelo}`}
                        onExpand={handleExpandImage}
                    />
                    
                    {/* Vehicle Details */}
                    <div className={styles.vehicleDetails} style={{ textAlign: 'center' }}>
                        <Text className={styles.vehicleInfo}>
                            {car.fecha_fabricacion} • {car.marca} {car.modelo}
                            {car.version && car.version.toUpperCase() !== 'SIN ESPECIFICAR' && ` • ${car.version}`}
                            {car.kilometraje && ` • ${car.kilometraje.toLocaleString()} km`}
                        </Text>
                    </div>
                    
                    <div className={styles.priceSection} style={{ textAlign: 'center' }}>
                        {car.rebaja && Number(car.rebaja) > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                <Text className={styles.price}>${Math.round(car.precio).toLocaleString('en-US', { maximumFractionDigits: 0 })}</Text>
                                <span
                                    style={{
                                        backgroundColor: '#22c55e',
                                        color: 'white',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    -{Number(car.rebaja)}%
                                </span>
                            </div>
                        ) : (
                            <Text className={styles.price}>${Math.round(car.precio).toLocaleString('en-US', { maximumFractionDigits: 0 })}</Text>
                        )}
                    </div>

                    <div className={styles.indicatorsSection}>
                        {/* Transparency Indicator */}
                        <div className={styles.indicatorCard} style={{ position: 'relative' }}>
                            <div className={styles.transparencyInfoIcon}>
                                <button
                                    className={styles.infoIcon}
                                    aria-label="Información sobre Puntuación de Transparencia"
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowTransparencyTooltip(!showTransparencyTooltip);
                                    }}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M12 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        <circle cx="12" cy="7" r="1" fill="currentColor"/>
                                    </svg>
                                </button>
                                {showTransparencyTooltip && (
                                    <>
                                        <div
                                            className={styles.tooltipBackdrop}
                                            onClick={() => setShowTransparencyTooltip(false)}
                                        />
                                        <div className={styles.transparencyTooltip}>
                                            <button
                                                className={styles.tooltipCloseButton}
                                                onClick={() => setShowTransparencyTooltip(false)}
                                                aria-label="Cerrar"
                                            >
                                                ✕
                                            </button>
                                            <div className={styles.tooltipHeader}>¿Qué es la Puntuación de Transparencia?</div>
                                            <div className={styles.tooltipText}>
                                                La <strong>Puntuación de Transparencia</strong> es un indicador clave que te ayuda a identificar las publicaciones más confiables y
                                                completas del mercado. Este score evalúa qué tan transparente es un vendedor con la información del vehículo que está
                                                ofreciendo.
                                            </div>

                                            <div className={styles.tooltipText}>
                                                Nuestro sistema analiza múltiples factores para generar esta puntuación:
                                            </div>
                                            <ul className={styles.tooltipList}>
                                                <li><strong>Reputación del vendedor:</strong> Historial de ventas y comportamiento en la plataforma</li>
                                                <li><strong>Calidad de la publicación:</strong> Completitud de los detalles del vehículo (especificaciones técnicas, kilometraje, historial de mantenimiento, fotos de calidad)</li>
                                                <li><strong>Documentación del cuidado:</strong> Disponibilidad de registros de servicio, facturas de mantenimiento</li>
                                            </ul>
                                        </div>
                                    </>
                                )}
                            </div>
                            <Text className={styles.indicatorTitle}>TRANSPARENCIA</Text>
                            <div className={styles.transparencyScore}>
                                <svg className={styles.progressRing} width="80" height="80">
                                    <circle
                                        className={styles.progressRingCircleBg}
                                        stroke="#3a3a3a"
                                        strokeWidth="6"
                                        fill="transparent"
                                        r="35"
                                        cx="40"
                                        cy="40"
                                    />
                                    <circle
                                        className={styles.progressRingCircle}
                                        stroke="#00d4d4"
                                        strokeWidth="6"
                                        fill="transparent"
                                        r="35"
                                        cx="40"
                                        cy="40"
                                        style={{
                                            strokeDasharray: `${2 * Math.PI * 35}`,
                                            strokeDashoffset: `${2 * Math.PI * 35 * (1 - ((car.transparencia || 0) / 100))}`
                                        }}
                                    />
                                </svg>
                                <div className={styles.scoreText}>
                                    {car.transparencia || 0}%
                                </div>
                            </div>
                            {car.transparencia_explicacion && (
                                <div className={styles.transparencyExplanationWrapper}>
                                    <button
                                        className={styles.infoIcon}
                                        aria-label="Explicación de Transparencia"
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowTransparencyExplanationTooltip(!showTransparencyExplanationTooltip);
                                        }}
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                            <path d="M12 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                            <circle cx="12" cy="7" r="1" fill="currentColor"/>
                                        </svg>
                                    </button>
                                    {showTransparencyExplanationTooltip && (
                                        <>
                                            <div
                                                className={styles.tooltipBackdrop}
                                                onClick={() => setShowTransparencyExplanationTooltip(false)}
                                            />
                                            <div className={styles.transparencyExplanationTooltip}>
                                                <button
                                                    className={styles.tooltipCloseButton}
                                                    onClick={() => setShowTransparencyExplanationTooltip(false)}
                                                    aria-label="Cerrar"
                                                >
                                                    ✕
                                                </button>
                                                <div className={styles.tooltipHeader}>Análisis de Transparencia</div>
                                                <div className={styles.tooltipText}>
                                                    {car.transparencia_explicacion}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Price Indicator */}
                        <div className={styles.indicatorCard}>
                            <Text className={styles.indicatorTitle}>PRECIO DE MERCADO</Text>
                            <div className={styles.priceIndicatorContent}>
                                <div className={styles.priceStatusIcon}>
                                    {getPriceStatus().status === 'unavailable' ? (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" fill={getPriceStatus().color} />
                                            <line
                                                x1="8"
                                                y1="12"
                                                x2="16"
                                                y2="12"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    ) : getPriceStatus().status === 'good' || getPriceStatus().status === 'fair' ? (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" fill={getPriceStatus().color} />
                                            <path
                                                d="M9 12l2 2 4-4"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    ) : (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" fill={getPriceStatus().color} />
                                            <line
                                                x1="12"
                                                y1="8"
                                                x2="12"
                                                y2="12"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                            />
                                            <circle cx="12" cy="16" r="1" fill="white" />
                                        </svg>
                                    )}
                                </div>
                                <Text className={styles.priceStatusLabel}>
                                    {getPriceStatus().label}
                                </Text>
                                {getPriceDifference() !== null && (
                                    <div className={styles.priceDifferenceContainer}>
                                        <Text className={styles.priceDifferenceLabel}>
                                            {(() => {
                                                const difference = getPriceDifference();
                                                if (difference === null) return "";
                                                if (difference > 0) {
                                                    return `$${Math.round(Math.abs(difference)).toLocaleString('en-US', { maximumFractionDigits: 0 })} por encima del mercado`;
                                                } else if (difference < 0) {
                                                    return `$${Math.round(Math.abs(difference)).toLocaleString('en-US', { maximumFractionDigits: 0 })} por debajo del mercado`;
                                                }
                                                return "En precio de mercado";
                                            })()}
                                        </Text>
                                        <div className={styles.tooltipWrapper}>
                                            <button
                                                className={styles.infoIcon}
                                                aria-label="Información sobre VMI"
                                                type="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setShowTooltip(!showTooltip);
                                                }}
                                            >
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                                    <path d="M12 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                    <circle cx="12" cy="7" r="1" fill="currentColor"/>
                                                </svg>
                                            </button>
                                            {showTooltip && (
                                                <>
                                                    <div
                                                        className={styles.tooltipBackdrop}
                                                        onClick={() => setShowTooltip(false)}
                                                    />
                                                    <div className={styles.tooltipFloatingPanel}>
                                                        <button
                                                            className={styles.tooltipCloseButton}
                                                            onClick={() => setShowTooltip(false)}
                                                            aria-label="Cerrar"
                                                        >
                                                            ✕
                                                        </button>
                                                        <div className={styles.tooltipHeader}>¿Qué es el VMI?</div>
                                                        <div className={styles.tooltipText}>
                                                            El <strong>VMI (Valor de Mercado Inteligente)</strong> es una referencia de
                                                            precio generada por inteligencia artificial, calculada a partir de un
                                                            análisis de miles de listados actuales e históricos de vehículos en
                                                            marketplaces y sitios web de dealers en tu mercado.
                                                        </div>
                                                        <ul className={styles.tooltipList}>
                                                            <li><strong>Considera variables como marca, modelo, versión, año, kilometraje, ubicación y tipo de vendedor.</strong></li>
                                                            <li><strong>Utiliza algoritmos avanzados y se actualiza continuamente para reflejar el mercado real.</strong></li>
                                                        </ul>
                                                        <div className={styles.tooltipText}>
                                                            El VMI debe usarse como una guía orientativa, ya que puede variar
                                                            dependiendo del estado real del vehículo, equipamiento especial e
                                                            información faltante.
                                                        </div>
                                                        <div className={styles.tooltipNote}>
                                                            <strong>Nota:</strong> El VMI no constituye una tasación oficial ni representa una
                                                            garantía de precio.
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Price Comparison Chart */}
                {car.precio_mercado_promedio && car.precio_mercado_promedio > 0 && (
                    <div className={styles.section} style={{ paddingTop: '8px', paddingBottom: '12px', marginTop: '-16px' }}>
                        <PriceComparison
                            precio={car.precio}
                            precio_mercado_promedio={car.precio_mercado_promedio}
                            precio_mercado_maximo={car.precio_mercado_maximo}
                        />
                    </div>
                )}

                {/* Características */}
                <div className={styles.section} style={{ paddingTop: '12px' }}>
                    <Text className={styles.sectionTitle}>Características</Text>
                    <div className={styles.characteristicsGrid}>
                        {/* Column 1 */}
                        <div className={styles.characteristicsColumn}>
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                                        <path d="M12 6v12M6 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Kilometraje</Text>
                                    <Text className={styles.characteristicValue}>{car.kilometraje?.toLocaleString()}</Text>
                                </div>
                            </div>
                            
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Color Exterior</Text>
                                    <Text className={styles.characteristicValue}>{car.color ? capitalizeFirstLetter(car.color.toLowerCase()) : 'No especificado'}</Text>
                                </div>
                            </div>
                            
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="1.5"/>
                                        <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="1.5"/>
                                        <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="1.5"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Tipo de Combustible</Text>
                                    <Text className={styles.characteristicValue}>{car.tipo_combustible ? capitalizeFirstLetter(car.tipo_combustible.toLowerCase()) : 'No especificado'}</Text>
                                </div>
                            </div>
                        </div>
                        
                        {/* Column 2 */}
                        <div className={styles.characteristicsColumn}>
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="9" cy="12" r="1" fill="currentColor"/>
                                        <circle cx="15" cy="12" r="1" fill="currentColor"/>
                                        <path d="M3 12h2.5M18.5 12H21M12 3v2.5M12 18.5V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.5"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Tipo Tracción</Text>
                                    <Text className={styles.characteristicValue}>{car.tipo_traccion || 'No especificado'}</Text>
                                </div>
                            </div>
                            
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="2" y="6" width="20" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <path d="M6 10v4M10 8v8M14 9v6M18 7v10" stroke="currentColor" strokeWidth="1.5"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Motor</Text>
                                    <Text className={styles.characteristicValue}>{car.motor || 'No especificado'}</Text>
                                </div>
                            </div>
                            
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 3h12l4 6-10 13L2 9l4-6z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <path d="M11 3L8 9l4 13 4-13-3-6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <line x1="2" y1="9" x2="22" y2="9" stroke="currentColor" strokeWidth="1.5"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Transmisión</Text>
                                    <Text className={styles.characteristicValue}>{car.transmision ? capitalizeFirstLetter(car.transmision.toLowerCase()) : 'No especificado'}</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Comentario del Experto */}
                <div className={styles.section} style={{ paddingTop: '12px' }}>
                    <Text className={styles.sectionTitle}>Comentario del Experto</Text>
                    <div className={styles.expertComment}>
                        <Text className={styles.expertCommentText}>
                            {car.comentario_experto || "No hay comentarios del experto disponibles para este vehículo."}
                        </Text>
                    </div>
                </div>

                {/* Contexto de mercado */}
                <div className={styles.section} style={{ paddingTop: '12px' }}>
                    <Text className={styles.sectionTitle}>Contexto de mercado</Text>
                    <div className={styles.marketStats}>
                        <div className={styles.marketStat}>
                            <Text className={styles.marketLabel}>Tiempo en mercado:</Text>
                            <Text className={styles.marketValue}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px', verticalAlign: 'middle' }}>
                                    <rect x="3" y="6" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M3 10H21" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M8 3V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <path d="M16 3V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                                {marketContext.daysOnMarket}
                            </Text>
                        </div>
                    </div>
                </div>

                {/* Importante */}
                <div className={styles.section} style={{ paddingTop: '12px' }}>
                    <Text className={styles.sectionTitle}>Importante</Text>
                    <div className={styles.characteristicsGrid}>
                        {/* Column 1 */}
                        <div className={styles.characteristicsColumn}>
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M7 12L12 7L17 12M12 7V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M7 17L12 22L17 17M12 22V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Cambio</Text>
                                    <Text className={styles.characteristicValue}>{car.acepta_cambios ? capitalizeFirstLetter(car.acepta_cambios.toLowerCase()) : 'No especificado'}</Text>
                                </div>
                            </div>
                            
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14.7 10.3a1 1 0 0 0 0-1.4l-1.6-1.6a1 1 0 0 0-1.4 0L8.2 10.8a2 2 0 0 0-.6 1.4V14a1 1 0 0 0 1 1h1.8a2 2 0 0 0 1.4-.6l3.5-3.5z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <path d="M10 9l3 3" stroke="currentColor" strokeWidth="1.5"/>
                                        <path d="M12 2l1.8 1.8a1 1 0 0 0 1.4 0l1.6-1.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <path d="M20 10l-1.8-1.8a1 1 0 0 0-1.4 0l-1.6 1.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <path d="M12 22l-1.8-1.8a1 1 0 0 0-1.4 0l-1.6 1.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <path d="M4 10l1.8 1.8a1 1 0 0 0 1.4 0l1.6-1.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Mantenimiento</Text>
                                    <Text className={styles.characteristicValue}>{car.mantenimiento ? capitalizeFirstLetter(car.mantenimiento.toLowerCase()) : 'No especificado'}</Text>
                                </div>
                            </div>
                            
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9 11H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <rect x="9" y="2" width="6" height="11" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <circle cx="12" cy="17" r="1" fill="currentColor"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Detalle Mantenimiento</Text>
                                    <Text className={styles.characteristicValue}>{car.mantenimiento_detalle ? capitalizeFirstLetter(car.mantenimiento_detalle.toLowerCase()) : 'No especificado'}</Text>
                                </div>
                            </div>
                        </div>
                        
                        {/* Column 2 */}
                        <div className={styles.characteristicsColumn}>
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="3" y="8" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <path d="M3 10h18" stroke="currentColor" strokeWidth="1.5"/>
                                        <path d="M7 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <path d="M7 6V4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="1.5"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Financiamiento</Text>
                                    <Text className={styles.characteristicValue}>{car.financiamiento_detalle ? capitalizeFirstLetter(car.financiamiento_detalle.toLowerCase()) : 'No especificado'}</Text>
                                </div>
                            </div>
                            
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 2L10.5 7.5L5 9L10.5 10.5L12 16L13.5 10.5L19 9L13.5 7.5L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
                                        <path d="M5 12L4 15L1 16L4 17L5 20L6 17L9 16L6 15L5 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
                                        <path d="M19 12L18 15L15 16L18 17L19 20L20 17L23 16L20 15L19 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Negociable</Text>
                                    <Text className={styles.characteristicValue}>{car.negociable ? capitalizeFirstLetter(car.negociable.toLowerCase()) : 'No especificado'}</Text>
                                </div>
                            </div>
                            
                            <div className={styles.characteristicItem}>
                                <div className={styles.characteristicIcon}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                        <path d="M4 21V19C4 16.7909 5.79086 15 8 15H16C18.2091 15 20 16.7909 20 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <path d="M12 11V21" stroke="currentColor" strokeWidth="1.5"/>
                                    </svg>
                                </div>
                                <div className={styles.characteristicContent}>
                                    <Text className={styles.characteristicLabel}>Único dueño</Text>
                                    <Text className={styles.characteristicValue}>{car.unico_dueno ? capitalizeFirstLetter(car.unico_dueno.toLowerCase()) : 'No especificado'}</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Análisis de Imágenes */}
                <div className={styles.section} style={{ paddingTop: '12px' }}>
                    <Text className={styles.sectionTitle}>Análisis de Imágenes</Text>
                    <div className={styles.expertComment}>
                        <Text className={styles.expertCommentText}>
                            {car.image_analysis || "No hay análisis de imágenes disponible para este vehículo."}
                        </Text>
                    </div>
                </div>

                {/* Vendedor */}
                <div className={styles.section} style={{ paddingTop: '12px' }}>
                    <Text className={styles.sectionTitle}>Vendedor</Text>
                    <div className={styles.vendorSection}>
                        <div className={styles.vendorHeader}>
                            <div className={styles.vendorBasicInfo}>
                                <Text className={styles.vendorName}>
                                    {car.vendedor_nombre || `${capitalizeFirstLetter(car.tipo_vendedor)} • ${capitalizeFirstLetter(car.departamento)}`}
                                </Text>
                                <Text className={styles.vendorType}>
                                    {capitalizeFirstLetter(car.tipo_vendedor)} • {capitalizeFirstLetter(car.departamento)}
                                </Text>
                            </div>
                        </div>
                        
                        {car.vendor_stats && (
                            <div className={styles.vendorStatsGrid}>
                                <div className={styles.vendorStatCard}>
                                    <div className={styles.vendorStatIcon}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M7 4V2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2h3a1 1 0 0 1 1 1v3.5a6.5 6.5 0 0 1-3.5 5.78V20a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-5.72A6.5 6.5 0 0 1 3 8.5V5a1 1 0 0 1 1-1h3Z" stroke="#0078d4" strokeWidth="1.5" fill="none"/>
                                            <path d="M9 2v2h6V2" stroke="#0078d4" strokeWidth="1.5"/>
                                        </svg>
                                    </div>
                                    <div className={styles.vendorStatContent}>
                                        <Text className={styles.vendorStatValue}>
                                            {car.vendor_stats.total_autos_vendidos || 0}
                                        </Text>
                                        <Text className={styles.vendorStatLabel}>AUTOS LISTADOS</Text>
                                    </div>
                                </div>
                                
                                {car.vendor_stats.marcas_vendidas && car.vendor_stats.marcas_vendidas.length > 0 && (
                                    <div className={styles.vendorStatCard}>
                                        <div className={styles.vendorStatIcon}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <rect x="3" y="6" width="18" height="14" rx="2" stroke="#0078d4" strokeWidth="1.5" fill="none"/>
                                                <path d="M3 10h18M7 2l1 4M17 2l-1 4M12 14v3M8 14l.5 3M16 14l-.5 3" stroke="#0078d4" strokeWidth="1.5" strokeLinecap="round"/>
                                            </svg>
                                        </div>
                                        <div className={styles.vendorStatContent}>
                                            <Text className={styles.vendorStatValue}>
                                                {car.vendor_stats.marcas_vendidas.length} 
                                            </Text>
                                            <Text className={styles.vendorStatLabel}>Marcas</Text>
                                        </div>
                                    </div>
                                )}
                                
                                {car.vendor_stats.fecha_ultimo_listado && (
                                    <div className={styles.vendorStatCard}>
                                        <div className={styles.vendorStatIcon}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="10" stroke="#0078d4" strokeWidth="1.5" fill="none"/>
                                                <path d="M12 6v6l4 2" stroke="#0078d4" strokeWidth="1.5" strokeLinecap="round"/>
                                            </svg>
                                        </div>
                                        <div className={styles.vendorStatContent}>
                                            <Text className={styles.vendorStatValue}>
                                                {(() => {
                                                    const lastDate = new Date(car.vendor_stats.fecha_ultimo_listado);
                                                    const today = new Date();
                                                    const diffDays = Math.floor((today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
                                                    if (diffDays === 0) return "Hoy";
                                                    if (diffDays === 1) return "Ayer";
                                                    if (diffDays < 7) return `${diffDays} días`;
                                                    if (diffDays < 30) {
                                                        const weeks = Math.floor(diffDays / 7);
                                                        return weeks === 1 ? "1 semana" : `${weeks} semanas`;
                                                    }
                                                    const months = Math.floor(diffDays / 30);
                                                    return months === 1 ? "1 mes" : `${months} meses`;
                                                })()}
                                            </Text>
                                            <Text className={styles.vendorStatLabel}>Último Auto Listado</Text>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {car.vendor_stats?.marcas_vendidas && car.vendor_stats.marcas_vendidas.length > 0 && (
                            <div className={styles.vendorBrandsSection}>
                                <Text className={styles.vendorBrandsTitle}>Marcas que maneja:</Text>
                                <div className={styles.vendorBrandsList}>
                                    {car.vendor_stats.marcas_vendidas.slice(0, 5).map((brand, index) => (
                                        <span key={index} className={styles.vendorBrandBadge}>
                                            {capitalizeFirstLetter(brand)}
                                        </span>
                                    ))}
                                    {car.vendor_stats.marcas_vendidas.length > 5 && (
                                        <span className={styles.vendorBrandBadgeMore}>
                                            +{car.vendor_stats.marcas_vendidas.length - 5} más
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Publication URL */}
                {car.url && (
                    <div className={styles.section} style={{ paddingTop: '12px' }}>
                        <Text className={styles.sectionTitle}>Publicación Original</Text>
                        <div className={styles.publicationUrlSection}>
                            <a 
                                href={car.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={styles.publicationUrlLink}
                            >
                                <div className={styles.publicationUrlContent}>
                                    <div className={styles.publicationUrlIcon}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M10 6H7c-1.11 0-2 .9-2 2v10c0 1.1.89 2 2 2h10c1.1 0 2-.9 2-2v-3M14 4h6m0 0v6m0-6L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <span className={styles.publicationUrlText}>Ver publicación original</span>
                                    <div className={styles.publicationUrlArrow}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                </div>
                                <div className={styles.publicationUrlDomain}>
                                    {(() => {
                                        try {
                                            const url = new URL(car.url);
                                            return url.hostname.replace('www.', '');
                                        } catch {
                                            return 'Ver sitio';
                                        }
                                    })()}
                                </div>
                            </a>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className={styles.actionSection}>
                    {/* Phone unavailable notice - shows when phone is not valid */}
                    {!hasValidPhone && (
                        <div className={styles.phoneUnavailableCard}>
                            <div className={styles.phoneUnavailableIcon}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <div className={styles.phoneUnavailableContent}>
                                <Text className={styles.phoneUnavailableTitle}>
                                    Teléfono no disponible
                                </Text>
                                <Text className={styles.phoneUnavailableText}>
                                    El número de contacto no fue proporcionado en este anuncio
                                </Text>
                            </div>
                            {car.url && (
                                <a
                                    href={car.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.phoneUnavailableButton}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M10 6H7c-1.11 0-2 .9-2 2v10c0 1.1.89 2 2 2h10c1.1 0 2-.9 2-2v-3M14 4h6m0 0v6m0-6L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Ver anuncio original
                                </a>
                            )}
                        </div>
                    )}

                    <Stack horizontal tokens={{ childrenGap: 12 }} horizontalAlign="center" style={{ width: '100%' }}>
                        {hasValidPhone && (
                            <PrimaryButton
                                className={styles.whatsappButton}
                                onClick={handleWhatsAppContact}
                                style={{
                                    backgroundColor: '#25D366',
                                    border: 'none',
                                    color: 'white'
                                }}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="white"
                                    style={{ marginRight: 8 }}
                                >
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                </svg>
                                WhatsApp
                            </PrimaryButton>
                        )}
                        <ActionButton
                            className={styles.saveButton}
                            onClick={handleSaveCar}
                            disabled={isSaving}
                            style={{
                                backgroundColor: isSaved ? '#f0f0f0' : undefined,
                                border: isSaved ? '1px solid #0078d4' : undefined,
                                opacity: isSaving ? 0.7 : 1
                            }}
                        >
                            {isSaving ? '...' : isSaved ? '✓ Guardado' : '⭐ Guardar'}
                        </ActionButton>
                        <PrimaryButton
                            className={styles.conversarButton}
                            onClick={handleConversarClick}
                            style={{
                                backgroundColor: '#FF6B35',
                                color: 'white',
                                border: 'none',
                                fontWeight: 600,
                                boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
                                minWidth: '120px'
                            }}
                        >
                            💬 Conversar
                        </PrimaryButton>
                    </Stack>
                </div>
            </div>
            </div>

            {/* Image Modal */}
            <ImageModalCarousel
                images={imageUrls}
                initialIndex={modalInitialIndex}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                carTitle={`${car.marca} ${car.modelo} ${car.fecha_fabricacion}`}
            />
        </>
    );
};